import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { UserDataType, UserLoginDataType } from '@app/core/models/types';
import { AuthService, LocalstorageService, UserService } from '@app/core/services';
import { LOGIN_FORM_LABELS } from '@app/core/models/constants/login-constants';
import { INPUT_EVENT_TYPES, LANGUAGES } from '@app/core/models/constants';
import * as questions from '../../../../assets/data/onboarding/es-questions.json';
import { InputType } from '@app/shared/ui-components';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  @ViewChild('inputEmail') inputEmail!: ElementRef<HTMLInputElement>;
  @ViewChild('inputPassword') inputPassword!: ElementRef<HTMLInputElement>;
  userLoginPayload: UserLoginDataType | undefined;
  userDataForm!: FormGroup;
  showPassword = false;
  loading = false;
  invalidGlobalMessage = '';
  emailErrorMessageSubject = new BehaviorSubject('');
  passwordErrorMessageSubject = new BehaviorSubject('');

  /** Input type*/
  inputType = InputType;

  /** Submitting flag */
  submitting = false;

  // For testing purposes - default to 6 questions
  private _totalQuestions = 6;

  constructor(
    private formBuilder: FormBuilder,
    private readonly _authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private localStorageService: LocalstorageService,
    private _userService: UserService,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.queryParams.subscribe(resp => {
      const lang = resp.lang;
      if (lang) {
        this.translate.use(lang);
        this.translate.setDefaultLang(lang);
        localStorage.setItem('LANGUAGE', JSON.stringify(LANGUAGES.find(language => language.code === lang)));
      }
    });
  }

  ngOnInit(): void {
    this.initForms();
    // Initialize total questions from the imported JSON file
    try {
      this._totalQuestions = Array.from(questions).length;
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  }

  get email(): any {
    return this.userDataForm.get('email');
  }

  get password(): any {
    return this.userDataForm.get('password');
  }

  initForms() {
    this.userDataForm = this.formBuilder.group({
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('[a-zA-Z0-9_.-]+([.][a-zA-Z0-9_.-]+)*@[a-zA-Z0-9_]+([.][a-zA-Z0-9_]+)*[.][a-zA-Z]{1,5}'), //TODO:  A validator file can be used for this, because we have a lot of email fields
        ]),
      ],
      password: ['', Validators.required],
    });
    this.listenInputs();
  }

  submit(form: FormGroup) {
    this.checkAllFields(form);
    if (!form.valid) return;

    const loginCredentials: UserLoginDataType = {
      username: this.email?.value.trim(),
      password: this.password?.value.trim(),
    };

    this.login(loginCredentials);
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  /**
   * Get the total number of onboarding questions
   * This method can be overridden in tests
   * @returns number of questions
   */
  protected getTotalQuestions(): number {
    return this._totalQuestions;
  }

  /**
   * Verify if the user has completed the onboarding process
   * @param tokenInfo
   */
  protected verifyOnboardingStatus(tokenInfo: any) {
    // Get total questions from the class property
    const totalQuestions = this.getTotalQuestions();

    // Check if onboarding is incomplete
    const isOnboardingIncomplete =
      !tokenInfo.user.onboarding ||
      !tokenInfo.user.onboarding.questions ||
      tokenInfo.user.onboarding.questions.length < totalQuestions;

    if (isOnboardingIncomplete) {
      this.router.navigate(['/onboarding']);

      return;
    }

    const localUserData = this.localStorageService.getLocalUserData();
    if (localUserData) {
      localUserData.onboardingComplete = true;
      this.localStorageService.setUserData(JSON.stringify(localUserData));
    }

    this.router.navigate(['/panel']);
  }

  private login(loginCredentials: UserLoginDataType) {
    this.loading = true;
    this.invalidGlobalMessage = '';
    this._authService.login(loginCredentials).subscribe({
      next: async (resp: any) => {
        const values: UserDataType = {
          account_status: resp.data.user.account_status,
          error: resp.error,
          full_name: resp.data.user.full_name,
          rol: resp.data.user.rol,
          user_id: resp.data.user.user_id,
          access_token: resp.data.tokens.access_token,
          refresh_token: resp.data.tokens.refresh_token,
        };
        this.localStorageService.setUserData(JSON.stringify(values));
        const tokenInfo = await this._authService.decodeToken();
        if (tokenInfo.user.avatar) {
          this.localStorageService.setItem('imgUser', JSON.stringify(tokenInfo.user.avatar!.url));
        }

        if (tokenInfo.user.lang) {
          this.translate.use(tokenInfo.user.lang);
          this.translate.setDefaultLang(tokenInfo.user.lang);
          localStorage.setItem(
            'LANGUAGE',
            JSON.stringify(LANGUAGES.find(language => language.code === tokenInfo.user.lang))
          );
        } else {
          const localLanguage = localStorage.getItem('LANGUAGE');
          if (localLanguage) {
            const language = JSON.parse(localLanguage);
            this._userService.setLanguage(tokenInfo.user._id, language.code).subscribe({
              next: resp => {
                if (!resp.error) {
                  values.access_token = resp.data.tokens.access_token;
                  values.refresh_token = resp.data.tokens.refresh_token;
                  this.localStorageService.setUserData(JSON.stringify(values));
                }
              },
              error: () => {},
            });
          }
        }
        this._authService.loggedIn.next(false);
        if (values.user_id) {
          this._userService.getOnboardingQuestions(values.user_id).subscribe({
            next: async response => {
              tokenInfo.user['onboarding'] = response.data.onboarding;
              this.loading = false;
              this.verifyInfoUsergStatus(tokenInfo);
            },
            error: () => {
              this.loading = false;
              tokenInfo.user['onboarding'] = { questions: [] };
              this.verifyInfoUsergStatus(tokenInfo);
            },
          });
        } else {
          // Handle case where user_id is missing
          this.loading = false;
          this.password.setValue('');
          this.invalidGlobalMessage = this.translateText('INCORRECT EMAIL OR PASSWORD PLEASE TRY AGAIN');
        }
      },
      error: error => {
        this.loading = false;
        // Handle different error formats
        if (
          error.status === 400 ||
          (error.error && error.error.statusCode === 400) ||
          error.status === 403 ||
          (error.error && error.error.statusCode === 403)
        ) {
          this.invalidGlobalMessage = this.translateText('USER NOT VERIFIED');
          const navigationExtras: NavigationExtras = { queryParams: { email: this.userDataForm.value.email.trim() } };
          this.router.navigate(['/register/verify-code'], navigationExtras);

          return;
        }

        if (error.status === 401 || (error.error && error.error.statusCode === 401)) {
          this.invalidGlobalMessage = this.translateText('INCORRECT EMAIL OR PASSWORD PLEASE TRY AGAIN');

          return;
        }

        this.password.setValue('');
        this.invalidGlobalMessage = this.translateText('UNEXPECTED ERROR');
      },
    });
  }
  /**
   * Verify if the user has completed the account info
   * @param tokenInfo
   */
  private verifyInfoUsergStatus(tokenInfo: any) {
    if (
      !tokenInfo.user.first_name ||
      tokenInfo.user.first_name === '' ||
      !tokenInfo.user.last_name ||
      tokenInfo.user.last_name === '' ||
      !tokenInfo.user.company ||
      !tokenInfo.user.company.companyName ||
      !tokenInfo.user.telephone ||
      tokenInfo.user.telephone === ''
    ) {
      this.router.navigate(['/register/account-info']);

      return;
    }

    const localUserData = this.localStorageService.getLocalUserData();
    if (localUserData) {
      localUserData.infoComplete = true;
      this.localStorageService.setUserData(JSON.stringify(localUserData));
    }

    this.verifyOnboardingStatus(tokenInfo);
  }

  private translateText(str: string) {
    return this.translate.instant('HOME.LOGIN_PAGE.' + str.toUpperCase());
  }

  private listenInputs() {
    // In test environment, ViewChild elements might not be available
    if (typeof jest !== 'undefined') {
      return; // Skip event listeners in test environment
    }

    const targetElement = this.inputEmail?.nativeElement;
    if (targetElement) {
      // Events for Email
      this.listenInputEmailEvents(INPUT_EVENT_TYPES.input);
      this.listenInputEmailEvents(INPUT_EVENT_TYPES.blur);
      // Events for Password
      this.listenInputPasswordEvents(INPUT_EVENT_TYPES.input);
      this.listenInputPasswordEvents(INPUT_EVENT_TYPES.blur);
    } else {
      setTimeout(() => {
        this.listenInputs();
      }, 100);
    }
  }

  private getEmailErrorMessage(eventType: string) {
    let errorMessage: string | null = null;

    if (eventType === INPUT_EVENT_TYPES.blur && this.email?.hasError('pattern')) {
      errorMessage = LOGIN_FORM_LABELS.EMAIL_ERROR.validEmail;
    }

    if (this.email?.hasError('required')) {
      errorMessage = LOGIN_FORM_LABELS.EMAIL_ERROR.required;
    }

    if (errorMessage) {
      this.email?.markAsTouched();
      this.emailErrorMessageSubject.next(this.translateText(errorMessage));
    }
  }

  private getPasswordErrorMessage() {
    let errorMessage: string | null = null;

    if (this.password?.hasError('required')) {
      errorMessage = LOGIN_FORM_LABELS.PASSWORD_ERROR.required;
    }

    if (errorMessage) {
      this.password?.markAsTouched();
      this.passwordErrorMessageSubject.next(this.translateText(errorMessage));
    }
  }

  private listenInputEmailEvents(eventType: string) {
    fromEvent(this.inputEmail?.nativeElement, eventType).subscribe(event => {
      this.getEmailErrorMessage(event.type);
    });
  }

  private listenInputPasswordEvents(eventType: string) {
    fromEvent(this.inputPassword?.nativeElement, eventType).subscribe(() => {
      this.getPasswordErrorMessage();
    });
  }

  private checkAllFields(form: FormGroup) {
    this.getEmailErrorMessage(INPUT_EVENT_TYPES.blur);
    this.getPasswordErrorMessage();
    Object.keys(form.controls).forEach(field => {
      const control = form.get(field);
      control?.updateValueAndValidity();
    });
  }
}
